import { Link, router, usePage } from "@inertiajs/react"
import { useState } from "react"
import Layout from "../components/Layout"
import Embed from "../components/Embed"
import LikeButton from "../components/LikeButton"
import PlatformLinks from "../components/PlatformLinks"
import Avatar from "../components/Avatar"
import { extractPostText } from "../lib/postText"

function detectEmbedType(url) {
  if (!url) return null
  if (url.match(/youtube\.com|youtu\.be/)) return "youtube"
  if (url.match(/open\.spotify\.com/)) return "spotify"
  if (url.match(/music\.apple\.com/)) return "apple_music"
  return "link"
}

function PostComposer() {
  const [text, setText] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const { current_user } = usePage().props
  const { body, url } = extractPostText(text)
  const previewEmbedType = detectEmbedType(url)
  const canSubmit = body.length > 0 || url.length > 0

  function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    router.post("/posts", { post: { body, url } }, {
      preserveScroll: true,
      onSuccess: () => setText(""),
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="border-b border-stone-800/60 pb-5 mb-1">
      <div className="flex gap-3">
        <Avatar user={current_user} className="mt-3" />
        <textarea
          placeholder="what's happening?"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          className="min-w-0 flex-1 bg-transparent px-0 py-2 text-lg text-stone-100 placeholder-stone-600 focus:outline-none resize-none leading-relaxed"
        />
      </div>

      {url && (
        <div className="pb-3 pl-12">
          <Embed url={url} embed_type={previewEmbedType} />
        </div>
      )}

      <div className="flex items-center justify-end">
        <button
          type="submit"
          disabled={submitting || !canSubmit}
          className="rounded-full bg-stone-100 px-5 py-2 text-sm font-semibold text-stone-950 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting ? "posting..." : "post"}
        </button>
      </div>
    </form>
  )
}

function PostCard({ post, isOwner }) {
  const date = new Date(post.created_at)
  const formatted = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })

  function handleDelete() {
    if (confirm("Delete this post?")) {
      router.delete(`/posts/${post.id}`)
    }
  }

  return (
    <article className="py-5 border-b border-stone-800/50 last:border-0">
      {post.author && (
        <div className="mb-3 flex items-center gap-2 text-sm text-stone-400">
          <Avatar user={post.author} size="sm" />
          <span className="font-medium text-stone-300">@{post.author.username}</span>
        </div>
      )}

      {post.body && (
        <Link href={`/posts/${post.id}`} className="block text-stone-200 transition-colors hover:text-white">
          <p className="leading-relaxed whitespace-pre-wrap">{post.body}</p>
        </Link>
      )}
      <Embed url={post.url} embed_type={post.embed_type} />
      <PlatformLinks post={post} />
      <div className="mt-3 flex items-center justify-between">
        <Link href={`/posts/${post.id}`} className="text-xs text-stone-600 transition-colors hover:text-stone-400">
          <time>{formatted}</time>
        </Link>
        <div className="flex items-center gap-2">
          <LikeButton post={post} />
          {isOwner && (
            <button
              onClick={handleDelete}
              className="text-xs text-stone-700 hover:text-red-500 transition-colors"
            >
              delete
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

export default function Feed({ posts }) {
  const { current_user } = usePage().props

  return (
    <Layout>
      <h1 className="mb-4 font-serif text-2xl text-cream">Share the music you love</h1>

      {current_user && <PostComposer />}

      {posts.length === 0 ? (
        <div className="py-12 text-center text-stone-600 text-sm">
          nothing here yet
        </div>
      ) : (
        <div>
          {posts.map(post => (
            <PostCard key={post.id} post={post} isOwner={post.owned_by_current_user} />
          ))}
        </div>
      )}
    </Layout>
  )
}
